import 'antd/dist/reset.css';
import 'mapbox-gl/dist/mapbox-gl.css'; // 引入Mapbox的CSS，确保地图正常显示
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { AntdAppProvider } from './utils/antdMessage';
import uiSettingsManager, { resolveMobileNavBarHeights, isMobileLayout } from './utils/uiSettings';
import { fetchGuestPermissions, fetchAnnouncements } from './utils/appPublicApi';

// 导入页面组件：路由表见 components/AppRoutes.js，导航栏见 components/AppMainNav.js
import AppRoutes from './components/AppRoutes';
import AppMainNav from './components/AppMainNav';

// 导入样式
import './App.css';

// 全局副作用（message 偏移、dayjs 扩展）：须在以上 import 之后执行，与拆分前一致
import './utils/appBootstrap';

/**
 * 应用主入口（App.js）
 * 功能：路由守卫，权限联动
 */


// 检测是否为手机端 - 改进版
// 与 uiSettings 共用同一套手机布局判断，避免 CSS 高度与 JSX 布局不一致
const isMobile = isMobileLayout;

// App主组件，负责路由和导航
function App() {
  const isTrackerBindPage = window.location.pathname === '/m/tracker';
  const isPickPointPage = window.location.pathname === '/m/pick';
  const isPickSharePage = /^\/p\/[^/]+$/.test(window.location.pathname);
  const isMobilePublicPage = isTrackerBindPage || isPickPointPage || isPickSharePage;
  const [dark, setDark] = useState(false);
  const [user, setUser] = useState(null); // 新增用户状态
  const [showTitle, setShowTitle] = useState(true); // 标题栏显示/隐藏状态
  const [currentPage, setCurrentPage] = useState(''); // 当前页面状态
  // 权限缓存刷新后递增，强制导航按最新 localStorage 重算 shouldShowPage
  const [permTick, setPermTick] = useState(0);
  
  // 公告状态
  const [announcements, setAnnouncements] = useState([]);
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);

  // 标题栏和导航栏设置状态
  const [titleBarText, setTitleBarText] = useState('龙泉市户外应急救援队 - 现场指挥中心');
  const [titleBarSettings, setTitleBarSettings] = useState({
    height: 60,
    fontSize: 24,
    backgroundColor: '#2c3e50',
    textColor: '#ffffff',
    letterSpacing: 0,
    fontWeight: 'bold'
  });
  const [navBarSettings, setNavBarSettings] = useState({
    height: 32,
    backgroundColor: '#4176a7',
    textColor: '#ffffff',
    fontSize: 16,
    linkSpacing: 32,
    linkPadding: 16
  });

  // 获取标题栏和导航栏设置
  const fetchUISettings = async () => {
    try {
      const settings = await uiSettingsManager.getAllSettings();
      // 必须与导航 JSX 使用同一套 isMobile()，避免仅缩窄窗口却套用手机端 76px 高度
      const useMobileLayout = isMobile();
      
      if (useMobileLayout) {
        // 手机端设置
        setTitleBarText(settings.mobileTitleBar?.text || settings.titleBar.text);
        setTitleBarSettings(settings.mobileTitleBar || settings.titleBar);
        setNavBarSettings(settings.mobileNavigationBar || settings.navigationBar);
      } else {
        // PC端设置
        setTitleBarText(settings.titleBar.text);
        setTitleBarSettings(settings.titleBar);
        setNavBarSettings(settings.navigationBar);
      }
      
      // 立即应用设置到CSS变量
      await applySettingsWithTitleBarState();
    } catch (error) {
      console.error('获取UI设置失败:', error);
    }
  };

  // 初始化时获取UI设置 - 强制从数据库重新获取
  useEffect(() => {
    console.log('App: 页面初始化，强制从数据库获取最新设置...');
    // 清理缓存，确保从数据库获取最新设置
    localStorage.removeItem('backendSystemSettings');
    localStorage.removeItem('systemSettingsLastFetch');
    fetchUISettings();
  }, []);

  // 监听窗口大小变化和系统设置更新，重新获取设置
  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      // 使用防抖，避免频繁调用
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(async () => {
        // 重新获取UI设置以适应新的屏幕尺寸
        await fetchUISettings();
        // 强制重新应用设置，确保手机端和PC端使用正确的设置
        applySettingsSyncWithTitleBarState();
      }, 300); // 300ms防抖
    };

    // 监听系统设置更新事件
    const handleSystemSettingsUpdated = async (event) => {
      console.log('收到系统设置更新事件:', event.detail);
      
      // 如果有传递的设置数据，直接使用，避免重新获取
      if (event.detail && event.detail.settings) {
        console.log('直接使用传递的设置数据:', event.detail.settings);
        
        // 直接应用传递的设置，而不是重新获取
        const settings = event.detail.settings;
        const useMobileLayout = isMobile();
        
        // 确定使用的设置
        const titleBar = useMobileLayout ? (settings.mobileTitleBar || settings.titleBar) : settings.titleBar;
        const navBar = useMobileLayout ? (settings.mobileNavigationBar || settings.navigationBar) : settings.navigationBar;
        
        if (useMobileLayout) {
          // 手机端设置
          setTitleBarText(settings.mobileTitleBar?.text || settings.titleBar.text);
          setTitleBarSettings(settings.mobileTitleBar || settings.titleBar);
          setNavBarSettings(settings.mobileNavigationBar || settings.navigationBar);
        } else {
          // PC端设置
          setTitleBarText(settings.titleBar.text);
          setTitleBarSettings(settings.titleBar);
          setNavBarSettings(settings.navigationBar);
        }
        
        // 直接应用传递的设置到CSS变量，不重新获取
        
        // 直接设置CSS变量
        document.documentElement.style.setProperty('--title-bar-height', `${titleBar.height}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-font-size', `${titleBar.fontSize}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-bg-color', titleBar.backgroundColor, 'important');
        document.documentElement.style.setProperty('--title-bar-text-color', titleBar.textColor, 'important');
        document.documentElement.style.setProperty('--title-bar-letter-spacing', `${titleBar.letterSpacing}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-font-weight', titleBar.fontWeight, 'important');
        
        if (useMobileLayout) {
          const mobileHeights = resolveMobileNavBarHeights(navBar);
          document.documentElement.style.setProperty('--nav-bar-height', `${mobileHeights.height}px`, 'important');
          document.documentElement.style.setProperty('--mobile-first-row-height', `${mobileHeights.firstRowHeight}px`, 'important');
          document.documentElement.style.setProperty('--mobile-second-row-height', `${mobileHeights.secondRowHeight}px`, 'important');
          document.documentElement.style.setProperty('--nav-bar-link-spacing', `${navBar.linkSpacing ?? 0}px`, 'important');
          document.documentElement.style.setProperty('--nav-bar-link-padding-x', `${navBar.linkPadding ?? 8}px`, 'important');
        } else {
          document.documentElement.style.setProperty('--nav-bar-height', `${navBar.height}px`, 'important');
          document.documentElement.style.setProperty('--nav-bar-link-spacing', `${navBar.linkSpacing ?? 0}px`, 'important');
          document.documentElement.style.setProperty('--nav-bar-link-padding-x', `${navBar.linkPadding ?? 16}px`, 'important');
        }
        document.documentElement.style.setProperty('--nav-bar-font-size', `${navBar.fontSize}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-bg-color', navBar.backgroundColor, 'important');
        document.documentElement.style.setProperty('--nav-bar-text-color', navBar.textColor, 'important');
        
        // 计算总头部高度
        const titleHeight = settings.titleBar.show ? titleBar.height : 0;
        const navHeight = useMobileLayout ? resolveMobileNavBarHeights(navBar).height : navBar.height;
        document.documentElement.style.setProperty('--header-total-height', `${titleHeight + navHeight}px`, 'important');
        
        console.log('App.js: 直接应用传递的设置，字体大小:', titleBar.fontSize + 'px');
      } else {
        // 如果没有传递设置数据，才重新获取
        console.log('没有传递设置数据，重新获取...');
        await fetchUISettings();
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('systemSettingsUpdated', handleSystemSettingsUpdated);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('systemSettingsUpdated', handleSystemSettingsUpdated);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // 初始化时获取访客权限和公告；已登录则从服务端刷新权限（避免后台改授权后仅刷新页面仍用旧缓存）
  useEffect(() => {
    const bootstrapPermissions = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        await fetchGuestPermissions();
        setPermTick((v) => v + 1);
        return;
      }
      try {
        const res = await fetch('/api/user/my-permissions', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          localStorage.setItem('permissions', JSON.stringify(data.permissions || []));
          localStorage.setItem('permissionConfig', JSON.stringify(data.permissionConfig || []));
          if (data.role) localStorage.setItem('role', data.role);
          const username = localStorage.getItem('username');
          if (username) {
            setUser({ username, permissions: data.permissions || [] });
          }
        } else {
          await fetchGuestPermissions();
        }
      } catch (error) {
        console.error('刷新登录权限失败:', error);
        await fetchGuestPermissions();
      }
      setPermTick((v) => v + 1);
    };
    bootstrapPermissions();
    const loadAnnouncements = async () => {
      const anns = await fetchAnnouncements();
      setAnnouncements(anns);
    };
    loadAnnouncements();
  }, []);

  // 公告轮播效果
  useEffect(() => {
    if (announcements.length > 1) {
      const timer = setInterval(() => {
        setCurrentAnnouncementIndex(prev => (prev + 1) % announcements.length);
      }, 5000); // 5秒切换一次
      return () => clearInterval(timer);
    }
  }, [announcements]);

  // 检查登录状态和权限
  useEffect(() => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    if (token && username) {
      setUser({ username, permissions });
    } else {
      setUser(null); // 确保访客用户状态为null
      // 如果是访客用户，获取访客权限
      fetchGuestPermissions();
    }
    document.body.className = dark ? 'dark' : '';
    
    // 设置body类
    if (!showTitle) {
      document.body.classList.add('no-title');
    } else {
      document.body.classList.remove('no-title');
    }
    
    // 初始化UI设置 - 考虑标题栏显示状态
    applySettingsSyncWithTitleBarState();
    
    // 无论是否登录，都异步拉取后台设置（公开接口 + 登录兜底）
    applySettingsWithTitleBarState().catch((error) => {
      console.error('应用系统设置失败:', error);
    });
  }, [dark, showTitle]);

  // 应用设置时考虑标题栏显示状态
  const applySettingsWithTitleBarState = async () => {
    try {
      // 获取当前设置
      const settings = await uiSettingsManager.getAllSettings();
      const useMobileLayout = isMobile();
      
      if (useMobileLayout) {
        // 手机端设置
        const titleBar = settings.mobileTitleBar || settings.titleBar;
        const navBar = settings.mobileNavigationBar || settings.navigationBar;

        setTitleBarText(titleBar.text || settings.titleBar?.text || '龙泉市户外应急救援队 - 现场指挥中心');
        setTitleBarSettings(titleBar);
        setNavBarSettings(navBar);
        
        // 应用手机端标题栏设置 - 使用更强的优先级
        document.documentElement.style.setProperty('--title-bar-height', `${titleBar.height}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-font-size', `${titleBar.fontSize}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-bg-color', titleBar.backgroundColor, 'important');
        document.documentElement.style.setProperty('--title-bar-text-color', titleBar.textColor, 'important');
        document.documentElement.style.setProperty('--title-bar-letter-spacing', `${titleBar.letterSpacing}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-font-weight', titleBar.fontWeight, 'important');
        
        // 应用手机端导航栏设置（第一行 / 第二行独立高度）
        const mobileHeights = resolveMobileNavBarHeights(navBar);
        document.documentElement.style.setProperty('--nav-bar-height', `${mobileHeights.height}px`, 'important');
        document.documentElement.style.setProperty('--mobile-first-row-height', `${mobileHeights.firstRowHeight}px`, 'important');
        document.documentElement.style.setProperty('--mobile-second-row-height', `${mobileHeights.secondRowHeight}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-bg-color', navBar.backgroundColor, 'important');
        document.documentElement.style.setProperty('--nav-bar-text-color', navBar.textColor, 'important');
        document.documentElement.style.setProperty('--nav-bar-font-size', `${navBar.fontSize}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-link-spacing', `${navBar.linkSpacing ?? 0}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-link-padding-x', `${navBar.linkPadding ?? 8}px`, 'important');
        
        // 手机端特有设置
        if (navBar.secondRowFontSize) {
          document.documentElement.style.setProperty('--mobile-second-row-font-size', `${navBar.secondRowFontSize}px`, 'important');
        }
        if (navBar.announcementFontSize) {
          document.documentElement.style.setProperty('--mobile-announcement-font-size', `${navBar.announcementFontSize}px`, 'important');
        }
        if (navBar.announcementColor) {
          document.documentElement.style.setProperty('--mobile-announcement-color', navBar.announcementColor, 'important');
        }
        if (navBar.buttonFontSize) {
          document.documentElement.style.setProperty('--mobile-button-font-size', `${navBar.buttonFontSize}px`, 'important');
        }
        if (navBar.buttonHeight) {
          document.documentElement.style.setProperty('--mobile-button-height', `${navBar.buttonHeight}px`, 'important');
        }
        if (navBar.buttonPadding) {
          document.documentElement.style.setProperty('--mobile-button-padding', navBar.buttonPadding, 'important');
        }
        
        // 计算总高度 - 考虑标题栏显示状态
        const titleHeight = showTitle ? titleBar.height : 0;
        const navHeight = mobileHeights.height;
        document.documentElement.style.setProperty('--header-total-height', `${titleHeight + navHeight}px`, 'important');
        
        console.log('手机端设置已应用，考虑标题栏状态:', { showTitle, titleHeight, navHeight, useMobileLayout, mobileHeights });
      } else {
        // PC端设置
        const titleBar = settings.titleBar;
        const navBar = settings.navigationBar;

        setTitleBarText(titleBar.text || '龙泉市户外应急救援队 - 现场指挥中心');
        setTitleBarSettings(titleBar);
        setNavBarSettings(navBar);
        
        // 应用PC端标题栏设置
        document.documentElement.style.setProperty('--title-bar-height', `${titleBar.height}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-font-size', `${titleBar.fontSize}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-bg-color', titleBar.backgroundColor, 'important');
        document.documentElement.style.setProperty('--title-bar-text-color', titleBar.textColor, 'important');
        document.documentElement.style.setProperty('--title-bar-letter-spacing', `${titleBar.letterSpacing}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-font-weight', titleBar.fontWeight, 'important');
        
        // 应用PC端导航栏设置
        document.documentElement.style.setProperty('--nav-bar-height', `${navBar.height}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-bg-color', navBar.backgroundColor, 'important');
        document.documentElement.style.setProperty('--nav-bar-text-color', navBar.textColor, 'important');
        document.documentElement.style.setProperty('--nav-bar-font-size', `${navBar.fontSize}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-link-spacing', `${navBar.linkSpacing ?? 0}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-link-padding-x', `${navBar.linkPadding ?? 16}px`, 'important');
        
        // 计算总高度 - 考虑标题栏显示状态
        const titleHeight = showTitle ? titleBar.height : 0;
        const navHeight = navBar.height;
        document.documentElement.style.setProperty('--header-total-height', `${titleHeight + navHeight}px`, 'important');
        
        console.log('PC端设置已应用，考虑标题栏状态:', { showTitle, titleHeight, navHeight, useMobileLayout, titleBar });
      }
    } catch (error) {
      console.error('应用设置失败:', error);
    }
  };

  // 同步应用设置时考虑标题栏显示状态
  const applySettingsSyncWithTitleBarState = () => {
    try {
      const settings = uiSettingsManager.getSettingsSync();
      const useMobileLayout = isMobile();
      console.log('同步应用设置，考虑标题栏状态:', { showTitle, useMobileLayout });
      
      if (useMobileLayout) {
        // 手机端设置
        const titleBar = settings.mobileTitleBar || settings.titleBar;
        const navBar = settings.mobileNavigationBar || settings.navigationBar;
        
        // 应用手机端标题栏设置 - 使用更强的优先级，确保背景色不被覆盖
        document.documentElement.style.setProperty('--title-bar-height', `${titleBar.height}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-font-size', `${titleBar.fontSize}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-bg-color', titleBar.backgroundColor, 'important');
        document.documentElement.style.setProperty('--title-bar-text-color', titleBar.textColor, 'important');
        document.documentElement.style.setProperty('--title-bar-letter-spacing', `${titleBar.letterSpacing}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-font-weight', titleBar.fontWeight, 'important');
        
        // 应用手机端导航栏设置（第一行 / 第二行独立高度）
        const mobileHeights = resolveMobileNavBarHeights(navBar);
        document.documentElement.style.setProperty('--nav-bar-height', `${mobileHeights.height}px`, 'important');
        document.documentElement.style.setProperty('--mobile-first-row-height', `${mobileHeights.firstRowHeight}px`, 'important');
        document.documentElement.style.setProperty('--mobile-second-row-height', `${mobileHeights.secondRowHeight}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-bg-color', navBar.backgroundColor, 'important');
        document.documentElement.style.setProperty('--nav-bar-text-color', navBar.textColor, 'important');
        document.documentElement.style.setProperty('--nav-bar-font-size', `${navBar.fontSize}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-link-spacing', `${navBar.linkSpacing ?? 0}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-link-padding-x', `${navBar.linkPadding ?? 8}px`, 'important');
        
        // 手机端特有设置
        if (navBar.secondRowFontSize) {
          document.documentElement.style.setProperty('--mobile-second-row-font-size', `${navBar.secondRowFontSize}px`, 'important');
        }
        if (navBar.announcementFontSize) {
          document.documentElement.style.setProperty('--mobile-announcement-font-size', `${navBar.announcementFontSize}px`, 'important');
        }
        if (navBar.announcementColor) {
          document.documentElement.style.setProperty('--mobile-announcement-color', navBar.announcementColor, 'important');
        }
        if (navBar.buttonFontSize) {
          document.documentElement.style.setProperty('--mobile-button-font-size', `${navBar.buttonFontSize}px`, 'important');
        }
        if (navBar.buttonHeight) {
          document.documentElement.style.setProperty('--mobile-button-height', `${navBar.buttonHeight}px`, 'important');
        }
        if (navBar.buttonPadding) {
          document.documentElement.style.setProperty('--mobile-button-padding', navBar.buttonPadding, 'important');
        }
        
        // 计算总高度 - 考虑标题栏显示状态
        const titleHeight = showTitle ? titleBar.height : 0;
        const navHeight = mobileHeights.height;
        document.documentElement.style.setProperty('--header-total-height', `${titleHeight + navHeight}px`, 'important');
        
        console.log('手机端同步设置已应用，考虑标题栏状态:', { showTitle, titleHeight, navHeight, useMobileLayout, mobileHeights });
      } else {
        // PC端设置
        const titleBar = settings.titleBar;
        const navBar = settings.navigationBar;
        
        // 应用PC端标题栏设置
        document.documentElement.style.setProperty('--title-bar-height', `${titleBar.height}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-font-size', `${titleBar.fontSize}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-bg-color', titleBar.backgroundColor, 'important');
        document.documentElement.style.setProperty('--title-bar-text-color', titleBar.textColor, 'important');
        document.documentElement.style.setProperty('--title-bar-letter-spacing', `${titleBar.letterSpacing}px`, 'important');
        document.documentElement.style.setProperty('--title-bar-font-weight', titleBar.fontWeight, 'important');
        
        // 应用PC端导航栏设置
        document.documentElement.style.setProperty('--nav-bar-height', `${navBar.height}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-bg-color', navBar.backgroundColor, 'important');
        document.documentElement.style.setProperty('--nav-bar-text-color', navBar.textColor, 'important');
        document.documentElement.style.setProperty('--nav-bar-font-size', `${navBar.fontSize}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-link-spacing', `${navBar.linkSpacing ?? 0}px`, 'important');
        document.documentElement.style.setProperty('--nav-bar-link-padding-x', `${navBar.linkPadding ?? 16}px`, 'important');
        
        // 计算总高度 - 考虑标题栏显示状态
        const titleHeight = showTitle ? titleBar.height : 0;
        const navHeight = navBar.height;
        document.documentElement.style.setProperty('--header-total-height', `${titleHeight + navHeight}px`, 'important');
        
        console.log('PC端同步设置已应用，考虑标题栏状态:', { showTitle, titleHeight, navHeight, useMobileLayout });
      }
    } catch (error) {
      console.error('同步应用设置失败:', error);
    }
  };

  // 退出登录
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('permissions');
    localStorage.removeItem('permissionConfig');
    localStorage.removeItem('role');
    localStorage.removeItem('command_authed'); // 清除指挥系统认证状态
    // 清掉无帐号后缀的轨迹显示缓存，避免下一指挥员首屏误用上一帐号的 display_days
    try {
      ['dashboard_trackMode', 'dashboard_trackMinutes', 'dashboard_trackHours', 'dashboard_trackDays',
        'dashboard_trackModeApplied', 'dashboard_lastAppliedTrackMode'].forEach((k) => localStorage.removeItem(k));
    } catch (_) { /* ignore */ }
    setUser(null);
    window.location.href = '/login'; // 跳转到登录页
  };

  // 切换标题栏显示/隐藏
  const toggleTitleBar = () => {
    setShowTitle(prev => !prev);
  };

  // 监听路由变化，自动设置当前页面状态
  useEffect(() => {
    const pathname = window.location.pathname;
    if (pathname === '/') {
      setCurrentPage('home');
    } else if (pathname === '/dashboard') {
      setCurrentPage('dashboard');
    } else if (pathname === '/tools') {
      setCurrentPage('tools');
    } else if (pathname === '/coordinate') {
      setCurrentPage('coordinate');
    } else if (pathname === '/rescue' || pathname === '/rescue-push') {
      setCurrentPage('rescue');
    } else if (pathname === '/multi-window-live' || pathname === '/live') {
      setCurrentPage('multi-window-live');
    } else if (pathname === '/admin') {
      setCurrentPage('admin');
    }
  }, [window.location.pathname]);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#1976d2',
        },
      }}
    >
      <AntdAppProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* 标题栏，支持显示/隐藏 */}
        {!isMobilePublicPage && showTitle && (
          <div className="header-title-bar">
            <div className="header-title-text">
              {titleBarText}
            </div>
          </div>
        )}
        
        {/* 导航栏 */}
        {!isMobilePublicPage && (
          <AppMainNav
            permTick={permTick}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            user={user}
            handleLogout={handleLogout}
            dark={dark}
            setDark={setDark}
            showTitle={showTitle}
            toggleTitleBar={toggleTitleBar}
            announcements={announcements}
            currentAnnouncementIndex={currentAnnouncementIndex}
          />
        )}
        {/* 路由配置 */}
        <AppRoutes />
      </Router>
      </AntdAppProvider>
    </ConfigProvider>
  );
}

export default App; 